import Analytics from '@opencare/analytics';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { COUNTRY } from 'common/constants';
import { getInsuranceList, getInsuranceListForCanada, requestClinic, updateBookingWidgetSlot } from 'connect/actions';
import { selectDataUserAuthenticated, selectInsuranceListForCountry } from 'connect/selectors';
import { CurrentStep } from 'pages/checkout/components/StepManager/CurrentStep';
import { SSO_REDIRECT_SEARCH_PARAM } from 'pages/checkout/constants';
import { selectDataClinic } from 'pages/profile/connect/selectors';

import { useTemporaryQueryParams } from 'utils/customHooks/use-query-params';

import { CheckoutGlobalStyle, CheckoutPageWrapper, CheckoutStepWrapper, TopSection } from './CheckoutPage-styled-components';
import { Navbar } from './components/Navbar/Navbar';
import { OfferBanner } from './components/OfferBanner/OfferBanner';
import { Progress } from './components/Progress/Progress';
import { resetCheckoutData } from './connect/helpers';

const analytics = new Analytics();

export const CheckoutPage = () => {
  const dispatch = useDispatch();
  const { params } = useRouteMatch();

  const isUserAuthenticated = useSelector(selectDataUserAuthenticated);

  const { clinicId: clinicIdString } = params as { clinicId: string; };
  const clinicId = parseInt(clinicIdString, 10);

  const ssoQueryParams = useTemporaryQueryParams([
    { variableName: 'ssoRedirect', paramKey: SSO_REDIRECT_SEARCH_PARAM },
    { variableName: 'isNewUser', paramKey: 'isNewUser' },
  ]);

  const isNewSsoUser = useMemo(
    () => ssoQueryParams.ssoRedirect && ssoQueryParams.isNewUser,
    [ssoQueryParams],
  );

  // Reset all saga loading and error states on first load
  // This makes sure we don't have any stale data from previous visits
  useEffect(() => {
    resetCheckoutData(dispatch);

    // Reset booking widget slot to acknowledge its been handled
    dispatch(updateBookingWidgetSlot(null, null, null));
  }, [dispatch]);

  const clinicData = useSelector(selectDataClinic);
  useEffect(() => {
    if (clinicData?.id === clinicId) {
      return;
    }

    // Either clinicData is not loaded or clinicId has changed
    dispatch(requestClinic(clinicId));
  }, [clinicId, clinicData, dispatch]);

  const insuranceList = useSelector(selectInsuranceListForCountry);
  useEffect(() => {
    if (insuranceList?.length) {
      return;
    }

    if (clinicData?.address?.country === COUNTRY.canada) {
      dispatch(getInsuranceListForCanada());
      return;
    }

    dispatch(getInsuranceList());
  }, [insuranceList, dispatch, clinicData?.address?.country]);

  useEffect(() => {
    if (!isNewSsoUser || !isUserAuthenticated) {
      return;
    }

    analytics.trackSignUp({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-page',
      name: 'checkout',
      section: 'sso-redirect',
    });
  }, [isNewSsoUser, isUserAuthenticated]);

  return (
    <>
      <CheckoutGlobalStyle />

      <CheckoutPageWrapper>
        <TopSection>
          <Navbar />
          <Progress />
        </TopSection>

        <CheckoutStepWrapper>
          <OfferBanner />
          <CurrentStep />
        </CheckoutStepWrapper>
      </CheckoutPageWrapper>
    </>
  );
};
